import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { hydrateServices, resolveServiceMediaUrl } from '../utils/serviceContent';
import { hasRichTextContent, prepareRichTextForDisplay } from '../utils/richText';
import './ProjectDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { locale, localePath } = useLocale();
  const { type, id } = useParams();
  const [project, setProject] = useState(null);
  const [serviceSlug, setServiceSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const serviceId = type === 'logo' ? 2 : type === 'uiux' ? 3 : type === 'plugin' ? 4 : 1;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/services?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => hydrateServices(response.data))
      .catch(() => hydrateServices([]))
      .then((services) => {
        if (cancelled) return;
        const service = services.find((item) => Number(item.legacyId) === serviceId);
        const foundProject = service?.works?.find((work) => String(work.id) === String(id) && work.isActive !== false) || null;
        setServiceSlug(service?.slug || '');
        setProject(foundProject);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, locale.code, serviceId]);

  if (loading) {
    return <div className="project-detail-page"><div className="container"><p>{t('common.loading')}</p></div></div>;
  }

  if (!project) {
    return (
      <div className="project-detail-page">
        <div className="container"><div className="project-not-found"><h2>{t('projectDetail.missing')}</h2><Link to={localePath('/services')} className="back-btn">{t('projectDetail.back')}</Link></div></div>
      </div>
    );
  }

  const hasDescription = hasRichTextContent(project.description, { allowVideo: true });
  const descriptionHtml = hasDescription
    ? prepareRichTextForDisplay(project.description, API_URL, { allowVideo: true })
    : '';

  return (
    <div className="project-detail-page">
      {project.image && (
        <div className="project-detail-header">
          <div className="project-header-image-wrapper">
            <img src={resolveServiceMediaUrl(project.image)} alt={project.title} className="project-header-image" />
          </div>
          {!!project.additionalImages?.length && (
            <div className="project-additional-images-overlay">
              {project.additionalImages.slice(0, 2).map((image, index) => (
                <button type="button" key={`${image}-${index}`} className="project-additional-image-thumb" onClick={() => setSelectedImage(image)}>
                  <img src={resolveServiceMediaUrl(image)} alt={t('projectDetail.imageNumber', { title: project.title, number: index + 1 })} className="project-additional-thumb-image" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedImage && (
        <div className="project-image-modal" onClick={() => setSelectedImage(null)}>
          <div className="project-image-modal-content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="project-image-modal-close ui-close-button" onClick={() => setSelectedImage(null)} aria-label={t('projectDetail.closeImage')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <img src={resolveServiceMediaUrl(selectedImage)} alt={t('common.largeImage')} className="project-image-modal-image" />
          </div>
        </div>
      )}

      <div className="container">
        <div className="project-detail-content">
          <div className="project-detail-title-section">
            <h1 className="project-detail-title">{project.title}</h1>
            {!!project.technologies?.length && (
              <div className="project-detail-technologies">
                {project.technologies.map((technology, index) => <span key={`${technology}-${index}`} className="project-tech-tag">{technology}</span>)}
              </div>
            )}
          </div>

          <div className="project-detail-description">
            {hasDescription
              ? <div className="project-detail-rich-text" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
              : <p>{t('projectDetail.noDescription')}</p>}
          </div>

          {project.imageBelowText && (
            <div className="project-detail-image-below-text">
              <img src={resolveServiceMediaUrl(project.imageBelowText)} alt={t('projectDetail.extraImage', { title: project.title })} className="project-image-below-text" />
            </div>
          )}

          <div className="project-detail-action-section">
            <Link to={localePath(`/services/${serviceSlug || serviceId}#pricing-plans`)} className="project-detail-order-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 10L8 13L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {t('projectDetail.order')}
            </Link>
            <Link to={localePath(`/services/${serviceSlug || serviceId}`)} className="project-detail-back-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 5L7.5 10L12.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {t('projectDetail.back')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
